import { AlertTriangle, ArrowRight, Boxes, CheckCircle2, PackagePlus, PanelTop, ShoppingBag } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../../lib/api'
import { formatDate } from '../../lib/format'
import type { AdminDashboardPayload } from '../../types/commerce'

export function AdminDashboard() {
  const [data, setData] = useState<AdminDashboardPayload | null>(null)
  const [error, setError] = useState('')

  useEffect(() => { adminApi.dashboard().then(setData).catch(err => setError(err.message)) }, [])

  if (error) return <div className="admin-page"><div className="form-error">{error}</div></div>
  if (!data) return <div className="admin-page"><div className="admin-loading">Carregando visão geral...</div></div>

  const cards = [
    { label: 'Produtos', value: data.products_total, helper: 'cadastrados na loja', Icon: Boxes },
    { label: 'Ativos', value: data.products_active, helper: 'visíveis no catálogo', Icon: CheckCircle2 },
    { label: 'Sem estoque', value: data.variants_out_of_stock, helper: 'variantes indisponíveis', Icon: AlertTriangle },
    { label: 'Pendentes', value: data.orders_pending, helper: 'pedidos pagos para processar', Icon: ShoppingBag }
  ]

  const quickActions = [
    { to: '/admin/produtos/novo', label: 'Cadastrar produto', helper: 'Fotos, preço, tamanhos e estoque', Icon: PackagePlus },
    { to: '/admin/conteudo', label: 'Editar vitrine', helper: 'Banners, categorias e navegação', Icon: PanelTop },
    { to: '/admin/pedidos', label: 'Ver pedidos', helper: 'Acompanhe o fluxo das vendas', Icon: ShoppingBag }
  ]

  const attention = data.products_total === 0
    ? { title: 'Cadastre o primeiro produto', text: 'Comece o catálogo com fotos, preço e pelo menos uma variante.', to: '/admin/produtos/novo', label: 'CADASTRAR', Icon: PackagePlus }
    : data.products_active === 0
      ? { title: 'Sua vitrine ainda está vazia', text: 'Ative um produto quando estiver pronto para exibi-lo na loja.', to: '/admin/produtos', label: 'REVISAR PRODUTOS', Icon: Boxes }
      : data.variants_out_of_stock > 0
        ? { title: 'Há itens aguardando reposição', text: 'Revise as variantes sem estoque para manter a experiência de compra fluida.', to: '/admin/produtos', label: 'VER ESTOQUE', Icon: AlertTriangle }
        : data.orders_pending > 0
          ? { title: 'Existem pedidos aguardando ação', text: 'Há pedidos com pagamento confirmado prontos para processamento.', to: '/admin/pedidos', label: 'VER PEDIDOS', Icon: ShoppingBag }
          : { title: 'A operação está em dia', text: 'Sem alertas de estoque ou pedidos pendentes neste momento.', to: '/admin/produtos', label: 'VER CATÁLOGO', Icon: CheckCircle2 }

  const AttentionIcon = attention.Icon

  return (
    <div className="admin-page admin-dashboard">
      <header className="admin-dashboard__hero">
        <div>
          <span className="admin-kicker">GK MAISON · PAINEL</span>
          <h1>Visão geral da loja</h1>
          <p>Um panorama limpo do que está publicado, do que precisa de atenção e dos próximos passos da operação.</p>
        </div>
        <Link className="admin-hero-link" to="/" target="_blank" rel="noreferrer">
          ABRIR LOJA <ArrowRight size={16} />
        </Link>
      </header>

      <section className="admin-metrics" aria-label="Indicadores da loja">
        {cards.map(({ label, value, helper, Icon }) => (
          <article className="admin-metric-card" key={label}>
            <div className="admin-metric-card__top">
              <span>{label}</span>
              <Icon size={17} />
            </div>
            <strong>{value}</strong>
            <small>{helper}</small>
          </article>
        ))}
      </section>

      {data.orders_awaiting_payment > 0 && <section className="admin-panel" style={{ marginBottom: 18 }}>
        <strong>{data.orders_awaiting_payment} pedido(s) aguardando pagamento</strong>
        <p>Pedidos não pagos continuam registrados para consulta, mas não devem ser enviados antes da confirmação.</p>
        <Link className="text-link" to="/admin/pedidos">VER SITUAÇÃO DOS PAGAMENTOS</Link>
      </section>}
      <section className="admin-editorial-grid">
        <article className="admin-focus-card">
          <div className="admin-focus-card__eyebrow">
            <span>PRÓXIMA AÇÃO</span>
            <div className="admin-focus-card__icon"><AttentionIcon size={20} /></div>
          </div>
          <div className="admin-focus-card__body">
            <h2>{attention.title}</h2>
            <p>{attention.text}</p>
          </div>
          <Link className="admin-focus-card__link" to={attention.to}>
            {attention.label} <ArrowRight size={16} />
          </Link>
        </article>

        <article className="admin-shortcuts">
          <div className="admin-shortcuts__heading">
            <span>ATALHOS</span>
            <h2>Gerencie sem perder tempo</h2>
          </div>
          <div className="admin-shortcuts__list">
            {quickActions.map(({ to, label, helper, Icon }, index) => (
              <Link to={to} key={to} className="admin-shortcut-row">
                <span className="admin-shortcut-row__number">0{index + 1}</span>
                <Icon size={18} />
                <div>
                  <strong>{label}</strong>
                  <small>{helper}</small>
                </div>
                <ArrowRight size={16} />
              </Link>
            ))}
          </div>
        </article>
      </section>

      <section className="admin-panel admin-orders-panel">
        <div className="panel-title-row admin-orders-panel__heading">
          <div>
            <span className="admin-kicker">MOVIMENTO RECENTE</span>
            <h2>Pedidos recentes</h2>
            <p>As últimas compras recebidas pela loja aparecem aqui.</p>
          </div>
          <Link className="text-link" to="/admin/pedidos">VER TODOS</Link>
        </div>

        {data.recent_orders.length === 0 ? (
          <div className="admin-empty admin-empty--editorial">
            <ShoppingBag size={24} />
            <strong>Ainda não há pedidos</strong>
            <p>Quando o primeiro pedido chegar, ele aparecerá aqui com cliente, itens, status e data.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table>
              <thead><tr><th>Pedido</th><th>Cliente</th><th>Itens</th><th>Status</th><th>Pagamento</th><th>Data</th></tr></thead>
              <tbody>
                {data.recent_orders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.order_number}</td>
                    <td>{order.customer_first_name} {order.customer_last_name}</td>
                    <td>{order.items_count}</td>
                    <td><span className={`status-pill status-${order.status}`}>{order.status}</span></td>
                    <td>{order.payment_status === 'aprovado' ? 'PAGO' : 'NÃO PAGO'}</td>
                    <td>{formatDate(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
